import React from 'react';

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";

import moment from "moment";


export default function MediaAttribution({ attribution, tl, dir, timestamp, cc = null }) {
	// helper: format seconds to HH:MM:SS.mmm for VTT
	const formatVttTime = (seconds) => {
			const h = Math.floor(seconds / 3600);
			const m = Math.floor((seconds % 3600) / 60);
			const s = Math.floor(seconds % 60);
			const ms = Math.floor((seconds - Math.floor(seconds)) * 1000);
			const hh = h.toString().padStart(2, '0');
			const mm = m.toString().padStart(2, '0');
			const ss = s.toString().padStart(2, '0');
			const mmm = ms.toString().padStart(3, '0');
			return `${hh}:${mm}:${ss}.${mmm}`;
	};

	// render cc tracks for the video
	const renderCC = () => {
		let createdTrackBlobUrl = null;

		if (cc && cc.length > 0) {
			try {
				// Build a WebVTT string
				let vtt = 'WEBVTT\n\n';
				cc.forEach((item, idx) => {
					// Compute start/end in seconds (fallback to 0)
					const start =
							(item.start_minute || 0) * 60 + (item.start_second || 0);
					const end = (item.end_minute || 0) * 60 + (item.end_second || 0);
					const startTime = formatVttTime(start);
					const endTime = formatVttTime(end);
					// sanitize the text - replace HTML tags/newlines with plain text line breaks
					const text = (item.tl_text || item.text || '')
						.replace(/<\/?[^>]+(>|$)/g, '')
						.replace(/\r\n|\r|\n/g, '\n');
					vtt += `${idx + 1}\n${startTime} --> ${endTime}\n${text}\n\n`;
				});

				// create a blob url and append a <track> element to the video
				const blob = new Blob([vtt], { type: 'text/vtt' });
				createdTrackBlobUrl = URL.createObjectURL(blob);
				return <track kind="subtitles" label="CC" srcLang="en" src={createdTrackBlobUrl} default />;
				// Note: appending a <track> often makes browsers show native captions UI.
			} catch (err) {
				console.warn('Failed to create VTT track:', err);
			}
    }
		return null;
	}

	return (
		<>
			{attribution.media_url && (
				<Box className='flex justify-center p-16 border-1 mx-16' sx={{ backgroundColor: '#F1F5F9', width: 'calc(100% - 32px)' }}>
					<div className="w-[640px] h-[360px] flex items-center justify-center overflow-hidden">
							<video width="640" height="360" controls>
								<source src={attribution.media_url} type="video/mp4" />
								{renderCC()}
							</video>
					</div>
				</Box>
			)}
			<Paper elevation={0} variant="outlined" square className="p-16 mx-16 mb-16">
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<Typography className="text-14 font-bold m-0">
							Media Attribution:
						</Typography>
						<Divider className="my-16" />
					</Grid>
					<Grid item container xs={12} spacing={1}>
						<Grid item xs={12} sm={4} lg={2}>
							<Typography className="text-14 font-semibold m-0">
								Title (TL):
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8} lg={10}>
							<Typography className={`${tl} m-0`} dir={dir}>
								{attribution.title_tl}
							</Typography>
						</Grid>		
					</Grid>
					<Grid item container xs={12} spacing={1}>
						<Grid item xs={12} sm={4} lg={2}>
							<Typography className="text-14 font-semibold m-0">
								Title (EN):
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8} lg={10}>
							<Typography className="text-14 m-0">
								{attribution.title_en}
							</Typography>
						</Grid>															
					</Grid>
					<Grid item container xs={12} spacing={1}>
						<Grid item xs={12} sm={4} lg={2}>
							<Typography className="text-14 font-semibold m-0">
								Time Stamp:
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8} lg={10}>
							<Typography className="text-14 m-0">
								{timestamp.start_minute}:
								{timestamp.start_second < 10 ? `0${timestamp.start_second}` : timestamp.start_second} -
								{timestamp.end_minute}:
								{timestamp.end_second < 10 ? `0${timestamp.end_second}`: timestamp.end_second} (trimmed from Authentic Material)
							</Typography>
						</Grid>
					</Grid>
					<Grid item container xs={12} spacing={1}>
						<Grid item xs={12} sm={4} lg={2}>
							<Typography className="text-14 font-semibold m-0">
								Source URL:
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8} lg={10}>
							<Typography className="text-14 m-0">
								{attribution.source_url}
							</Typography>
						</Grid>									
					</Grid>
					<Grid item container xs={12} spacing={1}>
						<Grid item xs={12} sm={4} lg={2}>
							<Typography className="text-14 font-semibold m-0">
								Author:
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8} lg={10}>
							<Typography className="text-14 m-0">
								{attribution.author}
							</Typography>
						</Grid>				
					</Grid>
					{attribution.author_desc_tl && (
						<Grid item container xs={12} spacing={1}>
							<Grid item xs={12} sm={4} lg={2}>
								<Typography className="text-14 font-semibold m-0">
									Speaker Description (TL):
								</Typography>
							</Grid>
							<Grid item xs={12} sm={8} lg={10}>
								<Typography className={`${tl} m-0`} dir={dir}>
									{attribution.author_desc_tl}
								</Typography>
							</Grid>
						</Grid>
					)}
					{attribution.author_desc_en && (
						<Grid item container xs={12} spacing={1}>
							<Grid item xs={12} sm={4} lg={2}>
								<Typography className="text-14 font-semibold m-0">
									Speaker Description (EN):
								</Typography>
							</Grid>
							<Grid item xs={12} sm={8} lg={10}>
								<Typography className="text-14 m-0">
									{attribution.author_desc_en}
								</Typography>
							</Grid>
						</Grid>
					)}
					<Grid item container xs={12} spacing={1}>
						<Grid item xs={12} sm={4} lg={2}>
							<Typography className="text-14 font-semibold m-0">
								Published Date:
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8} lg={10}>
							<Typography className="text-14 m-0">
								{moment.utc(attribution.published_date).local().format('MMM DD, YYYY')}
							</Typography>
						</Grid>						
					</Grid>
					<Grid item container xs={12} spacing={1}>
						<Grid item xs={12} sm={4} lg={2}>
							<Typography className="text-14 font-semibold m-0">
								Downloaded Date:
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8} lg={10}>
							<Typography className="text-14 m-0">
								{moment.utc(attribution.downloaded_date).local().format('MMM DD, YYYY')}
							</Typography>
						</Grid>						
					</Grid>
					<Grid item container xs={12} spacing={1}>
						<Grid item xs={12} sm={4} lg={2}>
							<Typography className="text-14 font-semibold m-0">
								Copyright License:
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8} lg={10}>
							<Typography className="text-14 m-0">
								{attribution.cc_license}
							</Typography>
						</Grid>								
					</Grid>
				</Grid>
			</Paper>				
		</>
	);
}
